// Clock: the time, drawn by its face (data.face):
//   digital  the time in large figures (seconds smaller, AM / PM beside it), the date and an optional caption below
//   analog   a round dial, always square (as big as the tile's shorter side allows), hour ticks and three hands
// Settings are toggles in its menu: Show Seconds (data.seconds), 24-Hour Time (data.h24), Show Date (data.date), and
// Background Color (data.color: transparent, white or black; the tile for digital, the dial for analog);
// Edit sets a time zone (data.zone, IANA, '' for the viewer's own) and a caption (data.label; a zone's city when
// empty). The kit's heartbeat ticks every clock on the second: each tile keeps a function (el.tick) that only updates
// the text or hands that changed, so nothing is rebuilt and an unchanged part is not repainted.
import {
  backgroundItem, choiceItem, editItem, el$, formDialog, heartbeat, isLight, lineIcon, setText, sizeOf, switchItem,
  textWidth,
} from './kit.js';

export default {
  kind: 'clock',
  label: 'Clock',
  icon: lineIcon('clock'),
  about: 'A digital or analog clock for any time zone',
  look: 'bare',
  sizes: ['2x1', '2x2', '3x2'], // the analog dial stays round, as big as the shorter side allows
  create: () => ({ data: {} }), // store.newShortcut fills in a digital clock in the viewer's time zone
  render: renderClock,
  menu: (s) => [
    choiceItem(s, 'Face', Object.entries(CLOCK_FACES), 'face', () => clockFace(s)),
    switchItem(s, 'Show Seconds', 'seconds', !!s.data?.seconds),
    clockFace(s) === 'digital' && switchItem(s, '24-Hour Time', 'h24', !!s.data?.h24),
    clockFace(s) === 'digital' && switchItem(s, 'Show Date', 'date', s.data?.date !== false),
    backgroundItem(s),
    editItem(clockDialog, s, fillClockDialog),
  ],
};

const CLOCK_FACES = { digital: 'Digital', analog: 'Analog' };
const CLOCK_PAD = 10; // around the face (.tile.clock)
const clockFace = (s) => (s.data?.face === 'analog' ? 'analog' : 'digital');
const clockFormats = new Map(); // `${zone}|${h24}` -> formatters, made once
function clockFormat(zone, h24) {
  const key = `${zone}|${h24}`;
  if (!clockFormats.has(key)) {
    const tz = zone ? { timeZone: zone } : {};
    clockFormats.set(key, {
      time: new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit', hourCycle: h24 ? 'h23' : 'h12', ...tz }),
      date: new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', ...tz }),
      hms: new Intl.DateTimeFormat('en-GB', { hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23', ...tz }),
    });
  }
  return clockFormats.get(key);
}
// The caption: the label, or the zone's city ("Asia/Tokyo" -> "Tokyo") when a zone is set.
const clockCaption = (s) => s.data?.label || (s.data?.zone ? s.data.zone.split('/').pop().replace(/_/g, ' ') : '');

function renderClock(s, el) {
  el.setAttribute('role', 'timer');
  el.setAttribute('aria-label', `Clock${clockCaption(s) ? `, ${clockCaption(s)}` : ''}`);
  el.dataset.face = clockFace(s);
  // data.color (Background Color): the whole tile behind digital figures, the dial's face for analog
  el.style.setProperty('--clock-bg', s.data?.color || 'transparent');
  el.classList.toggle('filled', !!s.data?.color);
  el.classList.toggle('light', !!s.data?.color && isLight(s.data.color));
  const { w, h } = sizeOf(s);
  (clockFace(s) === 'analog' ? drawAnalog : drawDigital)(s, el, w - 2 * CLOCK_PAD, h - 2 * CLOCK_PAD);
  el.tick();
  stopTicks ??= heartbeat(tickClocks);
}
function drawDigital(s, el, w, h) {
  const { seconds, h24, date } = s.data ?? {};
  const caption = clockCaption(s);
  const time = el$('div', 'clock-time');
  const main = el$('span', 'clock-main'), sec = el$('span', 'clock-sec'), period = el$('span', 'clock-period');
  time.append(main, sec, period);
  const lines = [date !== false && el$('div', 'clock-date'), caption && el$('div', 'clock-caption', caption)].filter(Boolean);
  // The figures as big as the room allows: by height (less for each line under them), then by the widest time
  const lineH = 18;
  let size = Math.min(72, (h - lines.length * lineH) * 0.78);
  const sample = `${h24 ? '00' : '10'}:00${seconds ? ':00' : ''}${h24 ? '' : ' PM'}`;
  size = Math.max(14, Math.min(size, size * (w / (textWidth(sample, size) * 1.04))));
  time.style.fontSize = `calc(${size.toFixed(1)}px * var(--s))`;
  el.replaceChildren(time, ...lines);
  const fmt = clockFormat(s.data?.zone, h24);
  el.tick = () => {
    const now = new Date();
    const parts = Object.fromEntries(fmt.time.formatToParts(now).map((p) => [p.type, p.value]));
    setText(main, `${parts.hour}:${parts.minute}`);
    setText(sec, seconds ? `:${parts.second}` : '');
    setText(period, h24 ? '' : ` ${parts.dayPeriod ?? ''}`);
    if (lines[0]?.className === 'clock-date') setText(lines[0], fmt.date.format(now));
  };
}
function drawAnalog(s, el, w, h) {
  const caption = clockCaption(s);
  const showCaption = caption && h >= 56; // under the dial, when the tile is tall enough to spare a line
  const d = Math.max(20, Math.min(w, h - (showCaption ? 16 : 0)));
  const ticks = Array.from({ length: 12 }, (_, i) => {
    const a = (i * Math.PI) / 6, r1 = i % 3 ? 41 : 38;
    return `<line class="clock-tick${i % 3 ? '' : ' major'}" x1="${50 + r1 * Math.sin(a)}" y1="${50 - r1 * Math.cos(a)}" x2="${50 + 45 * Math.sin(a)}" y2="${50 - 45 * Math.cos(a)}"/>`;
  }).join('');
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('class', 'clock-dial');
  svg.style.width = svg.style.height = `calc(${d}px * var(--s))`;
  svg.innerHTML = `<circle class="clock-face" cx="50" cy="50" r="48"/>${ticks}`
    + '<line class="clock-hand hour" x1="50" y1="54" x2="50" y2="27"/><line class="clock-hand minute" x1="50" y1="56" x2="50" y2="12"/>'
    + `${s.data?.seconds ? '<line class="clock-hand second" x1="50" y1="60" x2="50" y2="9"/>' : ''}<circle class="clock-pin" cx="50" cy="50" r="2.4"/>`;
  el.replaceChildren(svg, ...(showCaption ? [el$('div', 'clock-caption', caption)] : []));
  const [hour, minute, second] = ['hour', 'minute', 'second'].map((k) => svg.querySelector(`.${k}`));
  const fmt = clockFormat(s.data?.zone, true);
  const turn = (hand, deg) => {
    const t = `rotate(${deg.toFixed(2)} 50 50)`;
    if (hand && hand.getAttribute('transform') !== t) hand.setAttribute('transform', t); // the hour hand moves once a minute
  };
  el.tick = () => {
    const [H, M, S] = fmt.hms.format(new Date()).split(':').map(Number);
    turn(hour, (H % 12) * 30 + M * 0.5);
    turn(minute, M * 6 + S * 0.1);
    turn(second, S * 6);
  };
}
// Every clock on the webtop ticks on the heartbeat, subscribed while there is one (renderClock subscribes again).
let stopTicks = null;
function tickClocks() {
  const clocks = document.querySelectorAll('#webtop .tile.clock');
  if (!clocks.length) { stopTicks(); stopTicks = null; return; }
  clocks.forEach((el) => el.tick?.());
}

// Edit: the time zone (the viewer's own, or one of the zones this browser knows) and the caption.
const clockDialog = formDialog('clock', {
  title: 'Clock',
  fields: `
    <label>Time zone
      <select name="zone"></select>
    </label>
    <label>Caption
      <input type="text" name="label" maxlength="40" placeholder="Tokyo office">
    </label>`,
  check: (f) => ({ data: { zone: f.zone.value, label: f.label.value.trim().slice(0, 40) } }),
  setup(form) {
    const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    form.elements.zone.replaceChildren(
      new Option(`This device (${localZone.replace(/_/g, ' ')})`, ''),
      ...(Intl.supportedValuesOf?.('timeZone') ?? []).map((z) => new Option(z.replace(/_/g, ' '), z)),
    );
  },
});
function fillClockDialog(f, s) {
  f.zone.value = s.data?.zone ?? '';
  f.label.value = s.data?.label ?? '';
}
